import { useState, useEffect } from 'react';
import { Share2, Copy, Check, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { nativeShare } from '../lib/shareUtils';

export function ReferralShare({ userId }) {
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [referralCount, setReferralCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('referral_code, referral_count')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('Error fetching referral data:', error);
          return;
        }

        if (data) {
          setReferralCode(data.referral_code || '');
          setReferralCount(data.referral_count || 0);
        }
      } catch (error) {
        console.error('Error fetching referral data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchReferralData();
    }
  }, [userId]);

  const referralUrl = referralCode ? `https://gettilted.app?ref=${referralCode}` : '';

  const handleCopy = async () => {
    if (!referralUrl) return;
    
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    if (!referralUrl) return;
    
    const message = `Check out Tilted - the dive bar community app! Use my link: ${referralUrl}`;
    
    const shared = await nativeShare({
      title: 'Join me on Tilted',
      text: message,
      url: referralUrl
    });
    if (!shared) {
      window.location.href = `sms:?&body=${encodeURIComponent(message)}`;
    }
  };

  if (loading) {
    return (
      <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 animate-pulse">
        <div className="h-6 bg-dive-gray rounded w-1/3 mb-3" />
        <div className="h-4 bg-dive-gray rounded w-2/3 mb-4" />
        <div className="h-12 bg-dive-gray rounded" />
      </div>
    );
  }

  if (!referralCode) {
    return null; // Don't show if no referral code
  }

  return (
    <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-dive-amber" />
          Invite Friends
        </h3>
        {referralCount > 0 && (
          <span className="text-sm text-dive-amber font-medium">
            {referralCount} referred
          </span>
        )}
      </div>

      <p className="text-sm text-gray-400 mb-4">
        Share Tilted with friends and earn badges!
      </p>

      <div className="flex gap-2">
        <button
          onClick={handleShare}
          className="flex-1 bg-dive-amber hover:bg-amber-600 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <Share2 size={18} />
          Share Link
        </button>

        <button
          onClick={handleCopy}
          className="bg-dive-gray hover:bg-gray-600 text-white p-3 rounded-lg transition-colors"
          title="Copy link"
        >
          {copied ? (
            <Check size={18} className="text-green-400" />
          ) : (
            <Copy size={18} />
          )}
        </button>
      </div>

      <div className="mt-3 p-2 bg-dive-darker rounded-lg text-xs text-gray-400 text-center font-mono truncate">
        {referralUrl}
      </div>

      {/* Progress to Friend badge */}
      {referralCount < 5 && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          🤝 {5 - referralCount} more to earn "Friend of Tilted" badge
        </p>
      )}
      {referralCount >= 5 && referralCount < 10 && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          📣 {10 - referralCount} more to earn "Tilted Ambassador" badge
        </p>
      )}
    </div>
  );
}

export default ReferralShare;


